import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import type { MatchView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';

interface DeleteMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (matchId: number) => Promise<void> | void;
  match: MatchView | null;
}

const DeleteMatchModal: React.FC<DeleteMatchModalProps> = ({ isOpen, onClose, onDelete, match }) => {
  const { t, i18n } = useTranslation('tournament');
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleDelete = async () => {
    if (!match) return;
    setIsDeleting(true);
    try {
      await onDelete(match.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDateTime = (dateTimeString: string): string => {
    const date = parseApiWallClock(dateTimeString) ?? new Date(dateTimeString);
    return date?.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm" data-testid="delete-match-modal">
        {/* Header */}
        <DialogHeader className="py-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center me-3 flex-shrink-0">
              <Icon name="Trash2" size={20} className="text-red-600" />
            </div>
            <div>
              <DialogTitle className="font-bold">{t('Delete Match')}</DialogTitle>
              <DialogDescription>{t('This action cannot be undone')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <DialogBody>
          <div className="mb-4">
            <p className="text-foreground mb-4">
              {t('Are you sure you want to delete this match?')}
            </p>
            {match && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center text-sm">
                  <Icon name="Hash" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Match ID:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.id}</span>
                </div>
                <div className="flex items-start text-sm">
                  <Icon name="Trophy" size={14} className="text-gray-400 me-2 mt-0.5" />
                  <span className="font-medium text-foreground">{t('Opponents:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.team1Name} {t('vs')} {match?.team2Name}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Icon name="MapPin" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Location:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.location}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Icon name="Calendar" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Date & Time:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-gray-900">{formatDateTime(match?.scheduledDateTime ?? '')}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Icon name="Users" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Referee Team Members:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.refereeTeamMembers?.length} {t('members')}</span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
            <Icon name="AlertTriangle" size={18} className="text-red-600 me-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">
              {t('Deleting this match will permanently remove all associated data including team member assignments and match history.')}
            </p>
          </div>
        </DialogBody>

        {/* Footer */}
        <DialogFooter className="py-4 bg-muted/50 rounded-b-xl">
          <Button
            onClick={onClose}
            className="px-6 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
            data-testid="delete-match-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
            data-testid="delete-match-confirm-btn"
          >
            <Icon name="Trash2" size={16} className="me-2" />
            {isDeleting ? t('Deleting...') : t('Delete Match')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteMatchModal;
